<template>
  <aw-form v-model="v$" :class="$style.notificationItemCredit" @success="onSuccess">
    <div :class="$style.selectWrap">
      <aw-select-version2
        v-bind="validator.field"
        :widget-attrs="{ id: `${componentId}_selectedCredit` }"
        :inline-label-text="$awt('aw.loan_notification.select.label')"
        :options="selectableCredits"
        :multiple="false"
        option-id-property="baremId"
        option-label-property="baremName"
        :dropdown-icon-attrs="{
          name: 'arrow-down-16',
          size: 14
        }"
        :class="$style.select"
      />
      <aw-minimal-error v-bind="validator.error" />
    </div>
    <div :class="$style.description" v-text="notification.text.subtitle" />
    <div :class="$style.btnWrap">
      <aw-form-button v-model="v$" shrink styling="small-primary-dark">
        <span :class="$style.btnText" v-text="$awt('aw.loan_notification.btn_text')" />
      </aw-form-button>
    </div>
  </aw-form>
</template>

<script setup>
  import { useVuelidate } from '@vuelidate/core';
  import { useNuxtApp } from 'nuxt/app';
  import { useOrderStore } from '~~/common/stores/order';
  import AwForm from '~~/common/components/Form/AwForm.vue';
  import AwFormButton from '~~/common/components/Form/AwFormButton.vue';
  import AwSelectVersion2, { awSelectV2Props } from '~~/common/components/Common/AwSelectVersion2.vue';
  import AwMinimalError from '~~/common/components/Common/AwMinimalError.vue';
  import { watch } from 'vue';

  const props = defineProps({
    index: {
      type: String,
      required: true,
    },
    /**
     * @param {
     * {
     *    type: String,
     *    iconName: String,
     *    manualClose: Boolean,
     *    text: Object: {
     *      title: String,
     *      subtitle: String,
     *    }
     * }} notification
     */
    notification: {
      type: Object,
      default: () => ({ text: {} }),
    },
    close: {
      type: Function,
      default: () => {
      },
    },
    thinContent: {
      type: Boolean,
      default: false,
    },
    thickContent: {
      type: Boolean,
      default: false,
    },
    isMarginLess: {
      type: Boolean,
      default: false,
    },
    creditableButtons: {
      type: Array,
      default: () => [],
      validator (v) {
        if (!Array.isArray(v)) {
          return false;
        }
        return !v.some((x) => {
          return (
            (typeof x?.baremId !== 'number') ||
            (typeof x?.baremName !== 'string')
          );
        });
      },
    },
    initialCreditSelection: {
      type: Boolean,
      required: false,
    },
  });

  const { $awt } = useNuxtApp();

  const {
    removeAllUncreditableItem,
  } = useOrderStore();

  const componentId = props.notification?.uuid || '';

  const emits = defineEmits(['changed-barem-id']);

  const selectableCredits = computed(() => (Array.isArray(props.creditableButtons)
    ? [
      {
        baremId: -1,
        baremName: $awt('aw.loan_notification.select.default_value'),
      },
      ...props.creditableButtons,
    ]
    : []
  ));

  const selectedCredit = ref('');

  const v$ = useVuelidate(
    {
      selectedCredit: {},
    },
    {
      selectedCredit,
    },
  );
  const validator = computed(() => awSelectV2Props({
    validator: v$.value,
    validatorName: 'selectedCredit',
    model: selectedCredit,
    id: `${componentId}_selectedCredit_error`,
  }));

  watchEffect(
    () => {
      selectedCredit.value = selectableCredits.value[props.initialCreditSelection ? 1 : 0] || '';
    },
  );

  watch(
    selectedCredit,
    async (newVal, oldVal) => {
      const baremId = newVal?.baremId;
      if (baremId && (baremId !== oldVal?.baremId)) {
        emits('changed-barem-id', baremId);
      }
    },
    {
      immediate: true,
    },
  );

  function onSuccess () {
    const baremId = selectedCredit.value?.baremId;
    if (baremId) {
      removeAllUncreditableItem({
        baremId,
        resolveOption: 'auto',
      });
    }
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.notificationItemCredit {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 1px dashed $color-dash-separator;
  border-radius: 16px;
  background: $color-background-3;
  gap: 12px 20px;

  @include tablet(min) {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    display: grid;
    align-items: center;
    grid-template-areas:
      "select btn"
      "desc desc";
  }

  @include desktop-medium(min) {
    display: flex;
    flex-direction: row;
  }
}

.selectWrap {
  grid-area: select;
}

.description {
  grid-area: desc;
}

.btnWrap {
  grid-area: btn;
  justify-self: end;
}

.select {
  max-width: 264px;

  @include tablet(min) {
    width: 248px;
  }
}

.btnText {
  white-space: nowrap;
}
</style>
